/**
 * Weekly PPF-slot heatmap for the Appointments queue.
 *
 * Renders 6 rows (current ISO week + next 5). Each row shows the week
 * label, a Mon..Sun cell grid (count + intensity), and a "used / cap"
 * pill on the right. Sunday cells are visually muted since the shop is
 * closed. Clicking a day sets the queue's date filter so ops can drill
 * into that specific PPF backlog day.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ChevronDown, ChevronUp, Loader2, Shield } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';

const cellTone = (count) => {
  if (count <= 0) return 'bg-slate-50 text-slate-400 border-slate-200';
  if (count === 1) return 'bg-emerald-100 text-emerald-800 border-emerald-200';
  if (count === 2) return 'bg-amber-100 text-amber-800 border-amber-200';
  if (count === 3) return 'bg-orange-200 text-orange-900 border-orange-300';
  return 'bg-rose-500 text-white border-rose-500 shadow-sm';
};

const capPillTone = (used, cap) => {
  if (!cap) return 'bg-slate-50 text-slate-600 border-slate-200';
  if (used >= cap) return 'bg-rose-100 text-rose-800 border-rose-200';
  if (used >= cap - 1) return 'bg-amber-100 text-amber-800 border-amber-200';
  if (used === 0) return 'bg-slate-100 text-slate-500 border-slate-200';
  return 'bg-emerald-100 text-emerald-800 border-emerald-200';
};

const fmtWeekLabel = (startIso, endIso) => {
  const s = new Date(startIso + 'T00:00:00');
  const e = new Date(endIso + 'T00:00:00');
  const monthEq = s.getMonth() === e.getMonth();
  const sTxt = s.toLocaleString('en-IN', { day: '2-digit', month: 'short' });
  const eTxt = e.toLocaleString('en-IN', { day: '2-digit', month: monthEq ? undefined : 'short' });
  return monthEq ? `${sTxt} – ${e.getDate()}` : `${sTxt} – ${eTxt}`;
};

const isThisWeek = (startIso) => {
  const d = new Date();
  const mon = new Date(d);
  mon.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return mon.toISOString().slice(0, 10) === startIso;
};

const PpfHeatmap = ({ onPickDate }) => {
  const { authHeader } = useAuth();
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ cap: 4, ppf_services: [], weeks: [] });
  const [hover, setHover] = useState(null); // { date, bookings }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/appointments/stats/ppf-heatmap?weeks=6`, { headers: authHeader() });
      setData(d || { cap: 4, ppf_services: [], weeks: [] });
    } catch (e) { /* silent — ops can retry via reload */ }
    finally { setLoading(false); }
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => {
    const w = data.weeks || [];
    const active = w.reduce((acc, r) => acc + (r.used || 0), 0);
    const capped = w.filter((r) => r.cap > 0 && (r.used || 0) >= r.cap).length;
    return { active, capped, weeks: w.length };
  }, [data]);

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="ppf-heatmap">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition"
        data-testid="ppf-heatmap-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-teal-100 text-teal-700 flex items-center justify-center">
            <Shield size={16} />
          </div>
          <div className="text-left">
            <div className="text-sm font-bold text-slate-900">PPF backlog — next {totals.weeks || 6} weeks</div>
            <div className="text-[11px] text-slate-500">
              {totals.active} active PPF booking{totals.active === 1 ? '' : 's'} · {totals.capped} week{totals.capped === 1 ? '' : 's'} at cap ({data.cap ? `${data.cap}/wk combined` : 'no weekly limit'})
            </div>
          </div>
        </div>
        {open ? <ChevronUp size={16} className="text-slate-500" /> : <ChevronDown size={16} className="text-slate-500" />}
      </button>

      {open && (
        <div className="px-4 pb-4">
          {loading ? (
            <div className="text-xs text-slate-400 flex items-center gap-2 py-6 justify-center"><Loader2 size={12} className="animate-spin" /> Loading heatmap…</div>
          ) : (
            <div className="space-y-2">
              {/* Legend + column headers */}
              <div className="hidden sm:grid grid-cols-[140px_repeat(7,minmax(0,1fr))_88px] gap-1.5 items-center text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                <div />
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
                  <div key={d} className="text-center">{d}</div>
                ))}
                <div className="text-right">Used / Cap</div>
              </div>

              {(data.weeks || []).map((wk) => {
                const currentWeek = isThisWeek(wk.week_start);
                return (
                  <div
                    key={wk.week_start}
                    className="sm:grid sm:grid-cols-[140px_repeat(7,minmax(0,1fr))_88px] flex flex-col gap-1.5 items-stretch"
                    data-testid={`ppf-heatmap-week-${wk.week_start}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="text-xs font-semibold text-slate-700 truncate">{fmtWeekLabel(wk.week_start, wk.week_end)}</div>
                      {currentWeek && (
                        <span className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-teal-100 text-teal-700">Now</span>
                      )}
                    </div>
                    <div className="grid grid-cols-7 gap-1 sm:contents">
                      {wk.days.map((d) => {
                        const muted = d.is_sunday;
                        return (
                          <button
                            key={d.date}
                            type="button"
                            onMouseEnter={() => d.count > 0 && setHover({ date: d.date, bookings: d.bookings })}
                            onMouseLeave={() => setHover(null)}
                            onClick={() => onPickDate?.(d.date)}
                            className={`relative aspect-square sm:aspect-auto sm:min-h-[38px] rounded-md border text-xs font-bold tabular-nums transition hover:ring-2 hover:ring-teal-400 ${
                              muted ? 'bg-slate-100 text-slate-300 border-slate-200' : cellTone(d.count)
                            }`}
                            title={muted ? 'Closed this day' : `${d.date} · ${d.count} PPF booking${d.count === 1 ? '' : 's'}`}
                            data-testid={`ppf-heatmap-cell-${d.date}`}
                          >
                            <span className="absolute top-0.5 left-1 text-[9px] font-semibold opacity-70">{new Date(d.date + 'T00:00:00').getDate()}</span>
                            <span className="text-base">{muted ? '—' : d.count}</span>
                          </button>
                        );
                      })}
                    </div>
                    <div className="flex items-center justify-end">
                      <span className={`text-[11px] font-bold px-2 py-1 rounded-md border tabular-nums ${capPillTone(wk.used, wk.cap)}`} data-testid={`ppf-heatmap-cap-${wk.week_start}`}>
                        {wk.cap ? `${wk.used} / ${wk.cap}` : `${wk.used} booked`}
                      </span>
                    </div>
                  </div>
                );
              })}

              {/* Legend footer */}
              <div className="pt-2 border-t border-slate-100 flex flex-wrap items-center gap-3 text-[10px] font-semibold text-slate-500">
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-slate-50 border border-slate-200" /> 0</span>
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-100 border border-emerald-200" /> 1</span>
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-100 border border-amber-200" /> 2</span>
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-orange-200 border border-orange-300" /> 3</span>
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-rose-500 border border-rose-500" /> 4 (full)</span>
                <span className="ml-auto text-slate-400 hidden sm:inline">Click a day to filter the queue below</span>
              </div>

              {/* Hover popover — inline (not portal) so it stays cheap */}
              {hover && hover.bookings.length > 0 && (
                <div className="mt-2 bg-slate-900 text-slate-100 text-[11px] rounded-lg px-3 py-2" data-testid="ppf-heatmap-hover">
                  <div className="font-semibold text-teal-300 mb-1">{hover.date} · {hover.bookings.length} PPF booking{hover.bookings.length === 1 ? '' : 's'}</div>
                  <ul className="space-y-0.5">
                    {hover.bookings.map((b) => (
                      <li key={b.id}>
                        <span className="font-mono text-slate-300">{b.ref_no}</span>
                        <span className="mx-1 text-slate-500">·</span>
                        <span className="text-slate-200">{b.customer_name || '—'}</span>
                        <span className="mx-1 text-slate-500">·</span>
                        <span className="text-amber-300">{b.slot_window}</span>
                        <span className="mx-1 text-slate-500">·</span>
                        <span className="text-slate-400">{b.service_type}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PpfHeatmap;
